import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Pause, CheckCircle, X, Clock, Heart, Calendar } from 'lucide-react';
import { devLog } from '../utils/devLogger';

const DeactivateAccount = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [understood, setUnderstood] = useState(false);
  const [deactivated, setDeactivated] = useState(false);

  const handleDeactivate = () => {
    const today = new Date();
    const reactivationDeadline = new Date(today);
    reactivationDeadline.setDate(reactivationDeadline.getDate() + 90);

    // Update user status
    updateUser({
      accountStatus: 'dormant',
      deactivatedDate: today.toISOString(),
      reactivationDeadline: reactivationDeadline.toISOString(),
      subscriptionPaused: true
    });

    // Send deactivation email (TODO: wire to Postmark — see services/postmark_email.send_email)
    devLog(`📧 [mock] Deactivation email queued for ${user?.email} — reactivate by ${reactivationDeadline.toLocaleDateString()}`);

    setDeactivated(true);
  };

  if (deactivated) {
    const reactivationDate = new Date(user?.reactivationDeadline);

    return (
      <DashboardLayout>
        <div className="p-8 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-xl p-8">
            <div className="text-center mb-6">
              <CheckCircle size={64} className="mx-auto text-blue-600 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Deactivated</h1>
              <p className="text-gray-600">Your account is now in dormant status.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                <Calendar size={20} />
                Reactivation Window:
              </h3>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex justify-between">
                  <span>Deactivated:</span>
                  <span className="font-semibold">{new Date().toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Reactivation Deadline:</span>
                  <span className="font-semibold">{reactivationDate.toLocaleDateString()} (90 days)</span>
                </div>
                <div className="flex justify-between">
                  <span>Days Remaining:</span>
                  <span className="font-semibold">90 days</span>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-green-900 mb-3">What You Can Do:</h3>
              <div className="space-y-2 text-sm text-green-800">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Log in anytime to reactivate (instant, no setup)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>All your data will be waiting for you</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Subscription resumes from where it left off</span>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <p className="text-sm text-gray-600 mb-2">
                📧 <strong>Confirmation email sent to {user?.email}</strong>
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center mb-6">
              <p className="text-sm font-semibold text-purple-900 mb-2">Need to come back sooner?</p>
              <p className="text-sm text-purple-800 mb-3">
                Just visit hibff.com/login and sign in. Your account will automatically reactivate.
              </p>
            </div>

            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg"
            >
              Go to HiBFF Homepage
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <Pause size={64} className="mx-auto text-yellow-600 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Take a Break from HiBFF</h1>
            <p className="text-gray-600">
              Sometimes you need space. We get it. Growth isn&apos;t linear.
            </p>
          </div>

          {/* What Happens */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-blue-900 mb-4">What happens when you deactivate:</h3>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <div>
                  <p className="font-semibold text-blue-900">Your subscription is paused</p>
                  <p className="text-sm text-blue-800">No charges for 90 days</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <div>
                  <p className="font-semibold text-blue-900">Your data is safe</p>
                  <p className="text-sm text-blue-800">All AvAs, conversations, and progress preserved</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <div>
                  <p className="font-semibold text-blue-900">You can come back anytime</p>
                  <p className="text-sm text-blue-800">Just log in within 90 days</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <div>
                  <p className="font-semibold text-blue-900">After 90 days</p>
                  <p className="text-sm text-blue-800">If you haven&apos;t returned, we&apos;ll permanently delete your account</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Not a Cancellation */}
          <div className="p-6 bg-purple-50 border border-purple-200 rounded-xl mb-6">
            <p className="text-sm text-purple-900 mb-3">
              <strong>This is NOT a cancellation.</strong> You&apos;re not asking for a refund. You&apos;re just taking time apart.
            </p>
            <p className="text-sm text-purple-800">
              Like telling a friend &quot;I need some space right now, but I&apos;ll reach out when I&apos;m ready.&quot;
            </p>
          </div>

          {/* Examples When to Deactivate */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Examples of when to deactivate:</h3>
            <div className="space-y-2">
              {[
                "You're traveling and won't have time for HiBFF",
                "You're working with a personal coach and need to focus there",
                "You're overwhelmed and need to simplify your life",
                "You achieved your goals and want to see if you can maintain progress alone",
                "Life got busy and you need a break"
              ].map((example, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{example}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Examples When to Cancel */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Examples when to CANCEL instead (and get refund):</h3>
            <div className="space-y-2">
              {[
                "HiBFF isn't helping you",
                "You can't afford the subscription",
                "You found a better alternative",
                "Technical issues can't be resolved",
                "You're unhappy with the service"
              ].map((example, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <X size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span>{example}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate('/cancel-subscription')}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-semibold"
            >
              → Cancel subscription instead (with refund)
            </button>
          </div>

          {/* Real-Life Example */}
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl mb-6">
            <h3 className="font-bold text-yellow-900 mb-3 flex items-center gap-2">
              <Heart size={20} />
              Real-Life Example:
            </h3>
            <div className="text-sm text-yellow-800 space-y-2">
              <p><strong>Sarah, 28</strong> - Using HiBFF for exam stress</p>
              <p>• Deactivated: June 1 (exams over, needed break)</p>
              <p>• Reactivated: August 15 (new job stress)</p>
              <p><strong>Result:</strong> All her conversations, AvAs, and progress were waiting for her. No setup required.</p>
            </div>
          </div>

          {/* Consent Checkbox */}
          <div className="mb-6">
            <label className="flex items-start gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
              />
              <span className="text-sm text-gray-700">
                <strong>I understand my account will be deleted after 90 days if I don&apos;t reactivate</strong>
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
            >
              Nevermind, Keep Active
            </button>
            <button
              onClick={handleDeactivate}
              disabled={!understood}
              className="flex-1 py-3 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Deactivate My Account
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DeactivateAccount;
